import { AccountType } from "@common/constants/domains/accounting/accounts";
import { normalizeMatchableName } from "@common/helpers/string";
import { buildGroupPathSegments } from "@common/helpers/accounting";

interface CustomRowPairingRow {
    id: string;
    name: string;
    type: AccountType;
    // Ancestor group names, nearest parent first, ending at the top-level group.
    groupNamePath: string[];
    sortOrder: number;
}

interface PairCustomRowsParams {
    templateRows: CustomRowPairingRow[];
    entityRows: CustomRowPairingRow[];
}

interface PairBucketParams extends PairCustomRowsParams {
    depth: number;
    entityIdByTemplateId: Map<string, string>;
}

const ROOT_KEY = JSON.stringify(null);

const baseKey = (row: CustomRowPairingRow): string =>
    JSON.stringify([normalizeMatchableName(row.name), row.type]);

const ancestorKey = (row: CustomRowPairingRow, depth: number): string => {
    const ancestorName = row.groupNamePath.at(depth);
    return ancestorName === undefined
        ? ROOT_KEY
        : JSON.stringify(normalizeMatchableName(ancestorName));
};

const bySortOrder = (
    first: CustomRowPairingRow,
    second: CustomRowPairingRow,
): number =>
    first.sortOrder - second.sortOrder || first.id.localeCompare(second.id);

const groupRows = ({
    templateRows,
    entityRows,
    toKey,
}: PairCustomRowsParams & {
    toKey: (row: CustomRowPairingRow) => string;
}): Map<string, PairCustomRowsParams> => {
    const bucketByKey = new Map<string, PairCustomRowsParams>();
    const bucketFor = (row: CustomRowPairingRow): PairCustomRowsParams => {
        const key = toKey(row);
        const bucket = bucketByKey.get(key) ?? {
            templateRows: [],
            entityRows: [],
        };
        bucketByKey.set(key, bucket);
        return bucket;
    };
    templateRows.forEach((row) => bucketFor(row).templateRows.push(row));
    entityRows.forEach((row) => bucketFor(row).entityRows.push(row));
    return bucketByKey;
};

const pairBucket = ({
    templateRows,
    entityRows,
    depth,
    entityIdByTemplateId,
}: PairBucketParams): void => {
    if (templateRows.length === 0 || entityRows.length === 0) {
        return;
    }
    if (templateRows.length === 1 && entityRows.length === 1) {
        entityIdByTemplateId.set(templateRows[0].id, entityRows[0].id);
        return;
    }
    const isTiedAtRoot = [...templateRows, ...entityRows].every(
        (row) => row.groupNamePath.length <= depth,
    );
    if (isTiedAtRoot) {
        const sortedEntityRows = [...entityRows].sort(bySortOrder);
        [...templateRows].sort(bySortOrder).forEach((row, index) => {
            const entityRow = sortedEntityRows.at(index);
            if (entityRow !== undefined) {
                entityIdByTemplateId.set(row.id, entityRow.id);
            }
        });
        return;
    }
    const buckets = groupRows({
        templateRows,
        entityRows,
        toKey: (row) => ancestorKey(row, depth),
    });
    for (const bucket of buckets.values()) {
        pairBucket({ ...bucket, depth: depth + 1, entityIdByTemplateId });
    }
};

const pairCustomRows = ({
    templateRows,
    entityRows,
}: PairCustomRowsParams): Map<string, string> => {
    const entityIdByTemplateId = new Map<string, string>();
    const buckets = groupRows({ templateRows, entityRows, toKey: baseKey });
    for (const bucket of buckets.values()) {
        pairBucket({ ...bucket, depth: 0, entityIdByTemplateId });
    }
    return entityIdByTemplateId;
};

export interface CustomPairingAccount {
    id: string;
    name: string;
    type: AccountType;
    parentGroupId: string | null;
    sortOrder: number;
}

export interface CustomPairingGroup {
    id: string;
    name: string;
    parentGroupId: string | null;
}

interface CustomPairingChart {
    accounts: CustomPairingAccount[];
    groups: CustomPairingGroup[];
}

interface PairCustomAccountsParams {
    template: CustomPairingChart;
    entity: CustomPairingChart;
}

const toPairingRows = ({
    accounts,
    groups,
}: CustomPairingChart): CustomRowPairingRow[] =>
    accounts.map((account) => ({
        id: account.id,
        name: account.name,
        type: account.type,
        groupNamePath: buildGroupPathSegments(
            groups,
            account.parentGroupId,
        ).reverse(),
        sortOrder: account.sortOrder,
    }));

export const pairCustomAccounts = ({
    template,
    entity,
}: PairCustomAccountsParams): Map<string, string> =>
    pairCustomRows({
        templateRows: toPairingRows(template),
        entityRows: toPairingRows(entity),
    });
